// VIS-267 round 2 (H4): a KTX2 file in the GPU's own blocks (the phones' ASTC 6x6 far tree pictures, kit_package.py
// *_astc.ktx2) has zstd levels, and three r186's KTX2Loader unpacks those on the page's main thread with a zstd WASM
// decoder it keeps for the whole visit (KTX2Loader.js createRawTexture, module-level `_zstd`: a 16 MB heap on the phone).
// Here the same file is unpacked where it is called (ktx-unzstd-worker.js: a short-lived worker), into the same KTX2 with
// supercompression 0: three then reads it on its raw path with nothing to unpack, the very same level bytes.
// Pure (no DOM, no three): the worker and the unit test import it. Imports nothing: a module worker does not see the
// page's import map.

const MAGIC = [0xab, 0x4b, 0x54, 0x58, 0x20, 0x32, 0x30, 0xbb, 0x0d, 0x0a, 0x1a, 0x0a]; // «KTX 20»\r\n\x1A\n
const VK_UNDEFINED = 0, VK_ASTC_4x4_SFLOAT = 1000066000, ZSTD = 2; // (vkFormat 0 = Basis: its own transcoder worker unpacks it; ASTC 4x4 float may be Basis UASTC HDR)
const ALIGN = 16; // each level at a 16-byte boundary (any texel block size up to 16, and three's Float32/Uint16 views)
const u8 = (b) => (b instanceof Uint8Array ? b : ArrayBuffer.isView(b) ? new Uint8Array(b.buffer, b.byteOffset, b.byteLength) : new Uint8Array(b));

/** Is this a KTX2 file three reads on its raw path (no transcoder) with zstd levels? Reads the 80-byte header only. */
export function rawZstdKtx(buf) {
  const b = u8(buf);
  if (b.byteLength < 80 || MAGIC.some((x, i) => b[i] !== x)) return false;
  const v = new DataView(b.buffer, b.byteOffset, 80), vk = v.getUint32(12, true);
  return vk !== VK_UNDEFINED && vk !== VK_ASTC_4x4_SFLOAT && v.getUint32(44, true) === ZSTD;
}

/**
 * The file (ArrayBuffer or a view) with every level unpacked by decode(src: Uint8Array, n) -> Uint8Array of n bytes
 * (zstddec's ZSTDDecoder.decode). Everything before the levels (header, level index, DFD, key/values) is kept byte for
 * byte; supercompressionScheme becomes 0 and each level index entry names its unpacked bytes. Returns a Uint8Array over
 * its own ArrayBuffer (to move back to the page). Throws on a file that is not one, or a level that unpacks short.
 */
export function unzstdKtx(buf, decode) {
  const b = u8(buf);
  if (!rawZstdKtx(b)) throw new Error('KTX2 unzstd: not a raw KTX2 file with zstd levels');
  const v = new DataView(b.buffer, b.byteOffset, b.byteLength), n = Math.max(1, v.getUint32(40, true));
  const u64 = (o) => { if (v.getUint32(o + 4, true)) throw new Error('KTX2 unzstd: a level over 4 GB'); return v.getUint32(o, true); };
  const levels = Array.from({ length: n }, (_, i) => ({ i, off: u64(80 + 24 * i), len: u64(88 + 24 * i), raw: u64(96 + 24 * i) }));
  const head = Math.min(...levels.map((l) => l.off));
  if (head < 80 + 24 * n || levels.some((l) => l.off + l.len > b.byteLength || !(l.raw > 0))) throw new Error('KTX2 unzstd: the level index does not fit the file');
  let at = head; // the levels in the file's own order (KTX2 writes the smallest first)
  const place = [...levels].sort((x, y) => x.off - y.off).map((l) => { at = Math.ceil(at / ALIGN) * ALIGN; const p = at; at += l.raw; return [l, p]; });
  const out = new Uint8Array(at), w = new DataView(out.buffer);
  out.set(b.subarray(0, head));
  w.setUint32(44, 0, true); // supercompressionScheme: none
  for (const [l, p] of place) {
    const d = decode(b.subarray(l.off, l.off + l.len), l.raw);
    if (d.byteLength !== l.raw) throw new Error(`KTX2 unzstd: level ${l.i} unpacked to ${d.byteLength} of ${l.raw} bytes`); // (a cut or broken frame unpacks short)
    out.set(d, p);
    const o = 80 + 24 * l.i;
    w.setUint32(o, p, true); w.setUint32(o + 4, 0, true); // byteOffset
    w.setUint32(o + 8, l.raw, true); w.setUint32(o + 12, 0, true); // byteLength
    w.setUint32(o + 16, l.raw, true); w.setUint32(o + 20, 0, true); // uncompressedByteLength
  }
  return out;
}
